import { Array as YArray } from 'yjs';
import { PointData } from 'pixi.js';
import { View } from './view';
import { Gfx } from './gfx';
import { Colors } from './colors';
import { RoomSprite } from './room-sprite';
import { Kb } from 'src/util/kb';
import { Settings } from 'src/graphics/settings';
import { Account } from 'src/sync/account';
import { Store } from 'src/sync/store';
import { IToken, L, C } from 'src/sync/_api';
import { Util } from 'src/util/util';
import { Room } from 'src/sync/room';
import { Token } from './token';
import { Graffiti } from './graffiti';
import { booleanPointInPolygon, cleanCoords } from '@turf/turf';
import { CommandBarComponent } from 'src/app/command-bar/command-bar.component';

export enum MouseType { Free, Box, Triangle, Rectangle, Pentagon, Hexagon, Septagon, Octagon, Nonagon, Circle, Point, Door, Stair, Wall, Brush, Hull }

export class Mouse {
    public static downtime = Date.now();
    public static downtimeThreshold = 150; /* 150ms produces the best results, thoroughly tested */
    public static snapfactor = Settings.gridOffset;
    public static snapping = true;
    public static erasing = false;
    public static panning = false;
    public static leftdown = false;
    public static selecting = false;
    public static hovering = false;
    public static lasering = false;
    public static disabled = false;
    public static type = MouseType.Box;
    public static selectpoints: PointData[] = [];
    public static pasteTarget?: { token: IToken, offsetX: number, offsetY: number };
    public static laserButton = 2;
    public static panButton = 1;
    public static touches = 0;
    public static mouseFactor = 1;

    // TODO: Finish moving these last two items into the Store
    public static dragTarget?: RoomSprite;
    public static dragOffset?: PointData;

    public static get color() {
        if (Gfx.activeLayer.id == L.Fog) {
            return Mouse.erasing ? '0x8952ff' : '0x333333';
        } else if (Gfx.activeLayer.id == L.Tokens) {
            return Mouse.erasing ? '0xfd3b3b' : '0x20c997';
        } else {
            return Mouse.erasing ? Colors.red : Colors.blue;
        }
    }

    public static addEvents() {
        window.addEventListener('focus', () => {
            Mouse.disabled = true;
            setTimeout(() => Mouse.disabled = false, 10);
        });
        document.addEventListener('contextmenu', event => event.preventDefault());
        Mouse.addKeyboardEvents();
        Mouse.addMouseEvents();
        Account.settings.watch((x) => {
            const invalidLayerId = Object.keys(Gfx.layers).indexOf(x.activeLayerId) == -1;
            const needsAccessRemoved = Store.fullAccess.value == false && x.activeLayerId != L.Tokens;
            if (invalidLayerId || needsAccessRemoved) {
                /* if LayerId is invalid, switch to Token layer instead */
                Account.settings.modify(x => x.activeLayerId = L.Tokens);
            } else {
                Gfx.activeLayer = Gfx.layers[x.activeLayerId];
            }

            Mouse.laserButton = x.swapmiddleright ? 1 : 2;
            Mouse.panButton = x.swapmiddleright ? 2 : 1;
            Mouse.downtimeThreshold = x.requiredragselect ? 0 : 150;
            Mouse.erasing = x.erasing;
            Mouse.snapping = x.snapping;
            Mouse.type = x.mousetype;
            Mouse.snapfactor = Settings.gridOffset / x.alignment;
            Gfx.updateMapInteractivity();
            Gfx.updateTokenCursor(x.erasing, x.activeLayerId);
            Mouse.draw();
        });
    }

    public static snap(value: number, snapfactor = Mouse.snapfactor) {
        return Math.round(value / snapfactor) * snapfactor;
    }

    public static togglePanning(state: boolean) {
        Mouse.panning = state;
        Mouse.draw();
    }

    public static draw() {
        Gfx.mouse.clear();
        if (Mouse.panning) {
            document.body.style.cursor = 'grabbing';
            return;
        }

        document.body.style.cursor = 'default';
        Mouse.mouseFactor = (1 / View.viewport.scale.x);
        switch (Gfx.activeLayer.id) {
            // TODO: Use a custom pointer for Dungeons also, just like fog now does
            case L.Mapping:
                Gfx.mouse.circle(0, 0, 3 * Mouse.mouseFactor).fill({ color: Mouse.color });
                break;
            case L.Fog:
                if (Mouse.snapping) {
                    document.body.style.cursor = 'default';
                    Gfx.mouse.rect(-16 * Mouse.mouseFactor, 0, 32 * Mouse.mouseFactor, 2 * Mouse.mouseFactor).fill({ color: Mouse.color });
                    Gfx.mouse.rect(0, -16 * Mouse.mouseFactor, 2 * Mouse.mouseFactor, 32 * Mouse.mouseFactor).fill({ color: Mouse.color });
                } else {
                    document.body.style.cursor = Mouse.erasing
                        ? 'url(./assets/crosshair-purple.png) 16 16, auto'
                        : 'url(./assets/crosshair-gray.png) 16 16, auto';
                }
                break;
            case L.Tokens:
                if (Mouse.erasing) document.body.style.cursor = 'url(./assets/trashcan.png) 16 16, auto';
                break;
            case L.Graffiti:
                Gfx.mouse.circle(0, 0, (Graffiti.size() / 2) * Mouse.mouseFactor).stroke({ color: Mouse.color, width: Mouse.mouseFactor });
                if (Mouse.erasing) document.body.style.cursor = 'url(./assets/eraser.png) 0 32, auto';
                else document.body.style.cursor = 'none';
                break;
        }

        if (Mouse.selecting) {
            Mouse.drawSelection();
        }
    }

    public static drawSelection() {
        Gfx.mouse.clear();
        Gfx.selection.clear();
        Gfx.selection.circle(0, 0, 5 * (1 / View.viewport.scale.x)).fill(Mouse.color);
        Gfx.selection.stroke({ width: 2 * (1 / View.viewport.scale.x), color: '0x000000' });

        if (Mouse.type == MouseType.Free) {
            if (Mouse.selectpoints.length < 2) return;
            Mouse.drawSelectedPoints(Mouse.selectpoints.map(p => [p.x, p.y]));
        } else if (Mouse.type == MouseType.Hull) {
            if (Mouse.selectpoints.length < 2) return;
            const hull = Util.pointsToHull(Mouse.selectpoints);
            if (hull == null) return;
            Mouse.drawSelectedPoints(hull);
        } else if (Mouse.type == MouseType.Box) {
            let left = Gfx.mouse.position.x < Gfx.selection.x ? (Gfx.mouse.position.x - Gfx.selection.x) : 0;
            let top = Gfx.mouse.position.y < Gfx.selection.y ? (Gfx.mouse.position.y - Gfx.selection.y) : 0;
            let width = Math.abs(Gfx.mouse.position.x - Gfx.selection.x);
            let height = Math.abs(Gfx.mouse.position.y - Gfx.selection.y);
            Gfx.selection.rect(left, top, width, height);
        } else if (Mouse.type == MouseType.Circle) {
            Gfx.selection.circle(0, 0, Util.distance(Gfx.selection.position, Gfx.mouse.position));
        } else {
            const polygon = Mouse.getPolygonPath();
            Gfx.selection.poly(polygon);
        }
        Gfx.selection.stroke({ width: 2 * (1 / View.viewport.scale.x), color: Mouse.color });
    }

    public static drawSelectedPoints(points: number[][]) {
        const xOffset = Gfx.selection.position.x;
        const yOffset = Gfx.selection.position.y;

        const first = points[0];
        Gfx.selection.moveTo(first[0] - xOffset, first[1] - yOffset);
        for (let i = 1; i < points.length; i++) {
            const point = points[i];
            Gfx.selection.lineTo(point[0] - xOffset, point[1] - yOffset);
        }
        Gfx.selection.lineTo(first[0] - xOffset, first[1] - yOffset);
    }

    public static zoom(direction: 'in' | 'out') {
        const percent = direction == 'in' ? 0.2 : -0.2;
        View.viewport.zoomPercent(percent, true);
        View.redraw();
    }

    public static toggleErasing(erase?: boolean) {
        Account.settings.modify(x => {
            x.erasing = erase ?? !Mouse.erasing;
            Gfx.updateTokenCursor(x.erasing, x.activeLayerId);
        });
    }

    public static cycleAlignment(cycle: number = 1) {
        if (Store.fullAccess.value == false) return;
        const settings = Account.settings.get();
        const alignments = [1, 2, 3, 4, 5, 6, 8];
        let index = alignments.findIndex(x => x == settings.alignment) + cycle;
        if (index == alignments.length) index = 0;
        if (index == -1) index = alignments.length - 1;
        Account.settings.modify(x => x.alignment = alignments[index]);
    }

    public static setMouseType(type: MouseType) {
        const requiresFreedom = type == MouseType.Wall || type == MouseType.Stair || type == MouseType.Door;
        if (requiresFreedom && Store.fullAccess.value == false) return;
        Account.settings.modify(x => x.mousetype = type);
    }

    public static cancel(stopErasing = false) {
        Mouse.downtime = Date.now() - Mouse.downtimeThreshold - 1;
        Mouse.selecting = false;
        Mouse.selectpoints = [];
        Gfx.selection.clear();
        Mouse.deselectSprites();
        if (stopErasing && Mouse.erasing && Gfx.activeLayer.id == L.Tokens) {
            Mouse.toggleErasing(false);
        }
        Mouse.draw();
    }

    public static deselectSprites() {
        if (Store.multiselect.value) {
            for (let item of Store.multiselect.value) {
                item.deselect();
            }
            Store.multiselect.next(undefined);
        }
        if (Mouse.dragTarget == null) {
            Store.activeSprite.value?.deselect();
        }
    }

    public static updatePosition(globalX: number, globalY: number): PointData {
        const actual = View.viewport.toWorld({ x: globalX, y: globalY });
        let { x, y } = View.bindToWorld(actual);
        if (Mouse.snapping && Mouse.dragTarget == null) {
            x = Mouse.snap(x);
            y = Mouse.snap(y);
        }
        Gfx.mouse.position.copyFrom({ x, y });
        Store.mouse.next({ point: { x, y } });
        return { x, y };
    }

    public static beginSelection() {
        if (Gfx.activeLayer.cfg.selectable == false) return;

        Mouse.deselectSprites();
        Mouse.selecting = true;
        Mouse.selectpoints = [];
        Gfx.selection.position.copyFrom(Gfx.mouse.position);
    }

    public static finishSelection() {
        const path = Mouse.getSelectionPath();
        if (path.length == 0) return;

        // Bind to world
        for (let i = 0; i < path.length; i++) {
            path[i] = Math.min(Settings.worldSize, Math.max(0, path[i]));
        }

        Mouse.cancel();
        if (Gfx.activeLayer.id == L.Mapping || Gfx.activeLayer.id == L.Fog) {
            Gfx.activeLayer.addPath(path, !Mouse.erasing);
        } else {
            const polygon = Util.pathToMultiPolygon(path);
            const selected: Token[] = [];
            for (let id in Gfx.tokens) {
                const position = Gfx.tokens[id].data.position;
                if (booleanPointInPolygon([position.x, position.y], polygon) && Gfx.tokens[id].sprite.eventMode != 'none') {
                    selected.push(Gfx.tokens[id]);
                    Gfx.tokens[id].select(false);
                }
            }

            if (selected.length > 0) {
                if (Mouse.erasing) {
                    Room.document.transact(() => {
                        for (let item of selected) {
                            Room.tokens.delete(item.data.id);
                        }
                    }, L.Tokens);
                } else {
                    Store.multiselect.next(selected);
                }
            }
        }
        Mouse.draw();
    }

    private static getSelectionPath(): number[] {
        if (Mouse.type == MouseType.Free) {
            let poly = Util.pathToMultiPolygon(Mouse.selectpoints.map(p => [p.x, p.y]).flat(1));
            poly = cleanCoords(poly);
            return poly.geometry.coordinates.flat(3);
        } else if (Mouse.type == MouseType.Hull) {
            const hull = Util.pointsToHull(Mouse.selectpoints)?.flat(1) ?? [];
            return hull;
        } else if (Mouse.type == MouseType.Box) {
            return [
                Gfx.selection.x,
                Gfx.selection.y,
                Gfx.mouse.position.x,
                Gfx.selection.y,
                Gfx.mouse.position.x,
                Gfx.mouse.position.y,
                Gfx.selection.x,
                Gfx.mouse.position.y
            ];
        } else if (Mouse.type == MouseType.Circle) {
            return Util.circleInscribedPath(Gfx.selection.x, Gfx.selection.y, Util.distance(Gfx.mouse.position, Gfx.selection.position));
        } else {
            return Mouse.getPolygonPath(Gfx.selection);
        }
    }

    private static getPolygonPath(center: PointData = { x: 0, y: 0 }): number[] {
        let angle = Util.getRadianAngle(Gfx.selection.position, Gfx.mouse.position);
        let distance = Util.distance(Gfx.selection.position, Gfx.mouse.position);
        switch (Mouse.type) {
            case MouseType.Triangle:
                angle += (Math.PI / 6);
                return Util.circleInscribedPath(center.x, center.y, distance, 3, angle);
            case MouseType.Rectangle:
                return Util.circleInscribedPath(center.x, center.y, distance, 4, angle);
            case MouseType.Pentagon:
                angle -= (Math.PI / 10);
                return Util.circleInscribedPath(center.x, center.y, distance, 5, angle);
            case MouseType.Hexagon:
                angle += (Math.PI / 6);
                return Util.circleInscribedPath(center.x, center.y, distance, 6, angle);
            case MouseType.Septagon:
                angle += (Math.PI / 14);
                return Util.circleInscribedPath(center.x, center.y, distance, 7, angle);
            case MouseType.Octagon:
                return Util.circleInscribedPath(center.x, center.y, distance, 8, angle);
            case MouseType.Nonagon:
                angle = Util.getRadianAngle(Gfx.selection.position, Gfx.mouse.position) - (Math.PI / 18);
                return Util.circleInscribedPath(center.x, center.y, distance, 9, angle);
        }
        return [];
    }

    /* Document events are used because they fire even if the mouse leaves the viewport */
    private static addMouseEvents() {
        document.addEventListener('touchstart', (e) => Mouse.touches = e.touches.length);
        document.addEventListener('touchend', (e) => Mouse.touches = e.touches.length);
        document.addEventListener('touchcancel', (e) => Mouse.touches = e.touches.length);

        /* Must be mousedown, because pointerdown won't detect right mouse button */
        document.addEventListener('mousedown', (e) => { if (e.button == 2) Mouse.cancel(true); });

        /* Must be mouseup, because pointerup doesn't fire correctly if multiple mousebuttons are held */
        document.addEventListener('mouseup', (e) => {
            if (e.button == Mouse.laserButton) Mouse.lasering = false;
            if (e.button == Mouse.panButton) Mouse.togglePanning(false);
        });

        // NB: movementX values are incorrect in Pale Moon if we were to use pointermove instead
        document.addEventListener('mousemove', (e) => {
            if (Mouse.panning) {
                View.viewport.moveCenter(
                    View.viewport.center.x - (e.movementX * (1 / View.viewport.scale.x)),
                    View.viewport.center.y - (e.movementY * (1 / View.viewport.scale.y))
                );
                View.save();
            }
        });

        document.addEventListener('pointerup', (e) => {
            if (e.button == 0) {
                Mouse.leftdown = false;

                if (Mouse.dragTarget) {
                    if (Store.activeSprite.value == undefined && Store.multiselect.value == undefined) {
                        Mouse.dragTarget.deselect();
                    }
                    Mouse.dragTarget = undefined;
                }

                if (Store.isMobile.value == false && Date.now() - Mouse.downtime < Mouse.downtimeThreshold) {
                    Mouse.beginSelection();
                } else if (Mouse.selecting) {
                    Mouse.finishSelection();
                }
                Gfx.selection.clear();
            }
            Mouse.draw();
        });

        View.viewport.addEventListener('pointerdown', (e) => {
            if (Mouse.disabled) return;
            const position = Mouse.updatePosition(e.globalX, e.globalY);
            if (e.button == 0) {
                if (Store.activeSprite.value && Store.activeSprite.value?.sprite != e.target) {
                    Store.activeSprite.value.deselect();
                } else if (Mouse.dragTarget) {
                    return;
                } else if (Mouse.selecting) {
                    Mouse.finishSelection();
                } else {
                    Mouse.leftdown = true;
                    Mouse.downtime = Date.now();
                    Mouse.beginSelection();
                    if (Gfx.activeLayer.id == L.Graffiti) {
                        Graffiti.strokeTo(Store.mouse.value.point, Mouse.erasing, Mouse.mouseFactor);
                    }
                }
            } else if (e.button == Mouse.panButton) {
                Mouse.togglePanning(true);
            } else if (e.button == Mouse.laserButton) {
                Mouse.lasering = true;
            }
        });

        View.viewport.addEventListener('pointermove', (e) => {
            const position = Mouse.updatePosition(e.globalX, e.globalY);
            if (Mouse.lasering) {
                Mouse.updateLaser(View.bindToWorld(View.viewport.toWorld(e.global)));
            }

            if (Mouse.dragTarget && Mouse.dragOffset) {
                if (Mouse.touches > 1) return;
                Mouse.updateDrag(position, Mouse.dragTarget, Mouse.dragOffset, e.shiftKey);
            } else if (Mouse.selecting) {
                if (Mouse.type == MouseType.Free || Mouse.type == MouseType.Hull) {
                    Mouse.throttle(() => Mouse.selectpoints.push(position), 10);
                }
                Mouse.drawSelection();
            } else {
                if (Mouse.leftdown && Gfx.activeLayer.id == L.Graffiti) {
                    Graffiti.strokeTo(Store.mouse.value.point, Mouse.erasing, Mouse.mouseFactor);
                }
            }
        });
    }

    private static updateDrag(position: PointData, target: RoomSprite, offset: PointData, shift: boolean) {
        if (target.data.selectorId !== Account.id)
            return;

        let x = position.x - offset.x;
        let y = position.y - offset.y;

        const isToken = target.type === 'token';
        const tokenAdjustment = isToken ? (0.5 * target.sprite.width) : 0;

        if (shift !== true) {
            if (target.sprite.width <= Settings.gridOffset) {
                x = Mouse.snap(x, target.sprite.width);
                y = Mouse.snap(y, target.sprite.height);
            } else {
                x = Mouse.snap(x - tokenAdjustment, Settings.gridOffset) + tokenAdjustment;
                y = Mouse.snap(y - tokenAdjustment, Settings.gridOffset) + tokenAdjustment;
            }
        }

        if (target.sprite.width <= Settings.gridOffset) {
            x += offset.x;
            y += offset.y;
        }

        const isMultidrag = Store.multiselect.value?.some(x => x.data.id == Mouse.dragTarget?.data.id);
        if (isMultidrag == false) Store.multiselect.next(undefined);
        target.updatePosition({ x, y });
    }

    private static laserThrottle = false;
    private static updateLaser(point: PointData) {
        if (Mouse.laserThrottle) return;
        Mouse.laserThrottle = true;

        setTimeout(() => {
            const laser = Room.lasers.get(Account.id) as YArray<PointData>;
            const last = laser.get(laser.length - 1) ?? { x: -1, y: -1 };
            if (point.x != last.x || point.y != last.y) {
                laser.push([point]);
            }
            Mouse.laserThrottle = false;
        }, 25);
    }

    private static defaultThrottle = false;
    private static throttle(callback: Function, time: number) {
        if (Mouse.defaultThrottle) return;
        Mouse.defaultThrottle = true;

        setTimeout(() => {
            callback();
            Mouse.defaultThrottle = false;
        }, time);
    };

    private static addKeyboardEvents() {
        // TODO: Future hotkeys
        // Kb.on({ code: 'KeyR' }, () => EnableRuler);
        // Kb.on({ code: 'KeyA' }, () => PerformAttack);
        // Kb.on({ code: 'KeyC' }, () => Casting);

        // Kb.on({ code: 'KeyW' }, () => Mouse.setMouseType(MouseType.Wall));
        // Kb.on({ code: 'KeyV' }, () => Mouse.setMouseType(MouseType.Stair));
        // Kb.on({ code: 'KeyD' }, () => Mouse.setMouseType(MouseType.Door));
        // Kb.on({ code: 'KeyB' }, () => Mouse.setMouseType(MouseType.Brush));
        // Kb.on({ code: 'KeyBackquote' }, () => Mouse.setMouseType(MouseType.Point));
        Kb.on({ code: 'KeyH' }, () => Mouse.setMouseType(MouseType.Hull));
        Kb.on({ code: 'Digit1' }, () => Mouse.setMouseType(MouseType.Free));
        Kb.on({ code: 'Digit2' }, () => Mouse.setMouseType(MouseType.Box));
        Kb.on({ code: 'Digit3' }, () => Mouse.setMouseType(MouseType.Triangle));
        Kb.on({ code: 'Digit4' }, () => Mouse.setMouseType(MouseType.Rectangle));
        Kb.on({ code: 'Digit5' }, () => Mouse.setMouseType(MouseType.Pentagon));
        Kb.on({ code: 'Digit6' }, () => Mouse.setMouseType(MouseType.Hexagon));
        Kb.on({ code: 'Digit7' }, () => Mouse.setMouseType(MouseType.Septagon));
        Kb.on({ code: 'Digit8' }, () => Mouse.setMouseType(MouseType.Octagon));
        Kb.on({ code: 'Digit9' }, () => Mouse.setMouseType(MouseType.Nonagon));
        Kb.on({ code: 'Digit0' }, () => Mouse.setMouseType(MouseType.Circle));
        // Kb.on({ code: 'NumpadDecimal' }, () => Mouse.setMouseType(MouseType.Point));
        Kb.on({ code: 'Numpad1' }, () => Mouse.setMouseType(MouseType.Free));
        Kb.on({ code: 'Numpad2' }, () => Mouse.setMouseType(MouseType.Box));
        Kb.on({ code: 'Numpad3' }, () => Mouse.setMouseType(MouseType.Triangle));
        Kb.on({ code: 'Numpad4' }, () => Mouse.setMouseType(MouseType.Rectangle));
        Kb.on({ code: 'Numpad5' }, () => Mouse.setMouseType(MouseType.Pentagon));
        Kb.on({ code: 'Numpad6' }, () => Mouse.setMouseType(MouseType.Hexagon));
        Kb.on({ code: 'Numpad7' }, () => Mouse.setMouseType(MouseType.Septagon));
        Kb.on({ code: 'Numpad8' }, () => Mouse.setMouseType(MouseType.Octagon));
        Kb.on({ code: 'Numpad9' }, () => Mouse.setMouseType(MouseType.Nonagon));
        Kb.on({ code: 'Numpad0' }, () => Mouse.setMouseType(MouseType.Circle));
        Kb.on({ code: 'F12' }, () => Room.shout('pressed F12', C.Ref));
        Kb.on({ code: 'KeyC', ctrl: true, shift: true }, () => Room.shout('pressed Ctrl+Shift+C', C.Ref));
        Kb.on({ code: 'KeyI', ctrl: true, shift: true }, () => Room.shout('pressed Ctrl+Shift+I', C.Ref));
        Kb.on({ code: 'Equal', ctrl: false }, () => Mouse.zoom('in'));
        Kb.on({ code: 'Minus', ctrl: false }, () => Mouse.zoom('out'));
        Kb.on({ code: 'NumpadAdd', ctrl: false }, () => Mouse.zoom('in'));
        Kb.on({ code: 'NumpadSubtract', ctrl: false }, () => Mouse.zoom('out'));
        Kb.on({ code: 'KeyE' }, () => Mouse.toggleErasing());
        Kb.on({ code: 'Delete' }, () => Mouse.toggleErasing());
        Kb.on({ code: 'KeyC', ctrl: false }, (e) => { CommandBarComponent.instance?.open(); e.preventDefault(); });
        Kb.on({ code: 'KeyS', ctrl: false }, () => Account.settings.modify(x => x.snapping = !Mouse.snapping));
        Kb.on({ code: 'KeyA', ctrl: false, shift: false }, () => Mouse.cycleAlignment(1));
        Kb.on({ code: 'KeyA', ctrl: false, shift: true }, () => Mouse.cycleAlignment(-1));
        Kb.on({ code: 'KeyZ', ctrl: true, preventDefault: true }, () => Gfx.activeLayer.undoStack?.undo());
        Kb.on({ code: 'KeyY', ctrl: true, preventDefault: true }, () => Gfx.activeLayer.undoStack?.redo());
        Kb.on({ code: 'Space' }, () => Mouse.togglePanning(true), () => Mouse.togglePanning(false));
        const getMoveDistance = () => ((1 / View.viewport.scale.x) * Settings.gridOffset) / 1.5;
        Kb.on({ code: 'ArrowRight' }, () => {
            View.viewport.moveCenter(View.viewport.center.x + getMoveDistance(), View.viewport.center.y);
            View.save();
        });
        Kb.on({ code: 'ArrowLeft' }, () => {
            View.viewport.moveCenter(View.viewport.center.x - getMoveDistance(), View.viewport.center.y);
            View.save();
        });
        Kb.on({ code: 'ArrowUp' }, () => {
            View.viewport.moveCenter(View.viewport.center.x, View.viewport.center.y - getMoveDistance());
            View.save();
        });
        Kb.on({ code: 'ArrowDown' }, () => {
            View.viewport.moveCenter(View.viewport.center.x, View.viewport.center.y + getMoveDistance());
            View.save();
        });
        Kb.on({ code: 'Escape' }, () => Mouse.cancel(true));

        document.addEventListener('paste', (e) => {
            if (document.activeElement?.nodeName == 'INPUT') return;
            if (Mouse.pasteTarget && e.clipboardData?.types.length == 0) {
                const clone = Util.clone(Mouse.pasteTarget.token);
                clone.id = crypto.randomUUID();

                const mouse = Store.mouse.value.point;
                const offset = { x: mouse.x - Mouse.pasteTarget.offsetX, y: mouse.y - Mouse.pasteTarget.offsetY }
                clone.position = { x: Mouse.snap(offset.x), y: Mouse.snap(offset.y) };
                clone.position.x += Mouse.pasteTarget.offsetX;
                clone.position.y += Mouse.pasteTarget.offsetY;
                clone.selectorId = undefined;

                Room.document.transact(() => Room.tokens.set(clone.id, clone), L.Tokens);
                e.stopPropagation();
            }
        });
    }
}

